import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag } from "lucide-react";

import { useAuth } from "@/lib/auth";
import {
  REPORT_REASONS,
  REPORT_REASON_LABEL,
  createReport,
  hasOpenReport,
  type ReportReason,
  type ReportTargetType,
} from "@/lib/abuse-reports";
import type { ServiceType } from "@/lib/services";

export interface ReportTarget {
  targetType: ReportTargetType;
  listingId?: string | null;
  listingKind?: string | null;
  serviceType?: ServiceType | null;
  listingTitle?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
}

interface Props {
  target: ReportTarget;
  /** Optional label override for the trigger button. */
  label?: string;
  className?: string;
}

/** Trigger + modal that lets a signed-in user report a listing or a partner. */
export function ReportDialog({ target, label, className }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          "inline-flex items-center justify-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
        }
      >
        <Flag className="h-4 w-4" /> {label ?? "Report"}
      </button>
      {open && <ReportModal target={target} onClose={() => setOpen(false)} />}
    </>
  );
}

function ReportModal({ target, onClose }: { target: ReportTarget; onClose: () => void }) {
  const { user, profile } = useAuth();
  const [reason, setReason] = useState<ReportReason>("fake_listing");
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  const submit = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("auth");
      const open = await hasOpenReport(user.id, {
        targetType: target.targetType,
        listingId: target.listingId,
        partnerId: target.partnerId,
      });
      if (open) throw new Error("duplicate");
      await createReport({
        reporterId: user.id,
        reporterName: profile?.full_name ?? profile?.email ?? null,
        targetType: target.targetType,
        listingId: target.listingId ?? null,
        listingKind: target.listingKind ?? null,
        serviceType: target.serviceType ?? null,
        listingTitle: target.listingTitle ?? null,
        partnerId: target.partnerId ?? null,
        partnerName: target.partnerName ?? null,
        reason,
        comment,
      });
    },
    onSuccess: () => {
      setDone(true);
      toast.success("Report submitted. Our team will review it.");
    },
    onError: (e: Error) => {
      if (e.message === "duplicate") {
        toast.error("You already have an open report for this. We'll update you soon.");
      } else {
        toast.error("Could not submit the report. Please try again.");
      }
    },
  });

  const title = target.targetType === "listing" ? "Report this listing" : "Report this partner";
  const subject = target.listingTitle ?? target.partnerName ?? "";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-brown-dark/40 p-0 sm:items-center sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-t-3xl border border-border bg-card p-4 shadow-soft sm:rounded-3xl sm:p-5"
      >
        <h2 className="text-base font-bold text-brown-dark">{title}</h2>
        {subject && <p className="mt-1 truncate text-xs text-muted-foreground">{subject}</p>}

        {!user ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground">Please sign in to submit a report.</p>
            <Link
              to="/login"
              className="inline-flex w-full items-center justify-center rounded-full bg-brand px-4 py-2 text-sm font-bold text-brown-dark"
            >
              Sign in
            </Link>
            <CloseBtn onClose={onClose} />
          </div>
        ) : done ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Thanks for flagging this. You can follow the status under{" "}
              <span className="font-semibold text-brown-dark">My Reports</span> and we'll notify you
              once it's reviewed.
            </p>
            <CloseBtn onClose={onClose} label="Done" />
          </div>
        ) : (
          <form
            className="mt-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              submit.mutate();
            }}
          >
            <fieldset className="space-y-1.5">
              <legend className="text-xs font-semibold text-brown-dark">Reason</legend>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {REPORT_REASONS.map((r) => (
                  <label
                    key={r}
                    className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs transition ${
                      reason === r
                        ? "border-brand bg-brand-soft font-semibold text-brown-dark"
                        : "border-border"
                    }`}
                  >
                    <input
                      type="radio"
                      name="report-reason"
                      className="accent-current"
                      checked={reason === r}
                      onChange={() => setReason(r)}
                    />
                    {REPORT_REASON_LABEL[r]}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="block text-xs font-semibold text-brown-dark">
              Comment (optional)
              <textarea
                value={comment}
                maxLength={1000}
                rows={3}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell us what's wrong…"
                className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-normal outline-none focus:ring-2 focus:ring-brand"
              />
            </label>

            <button
              type="submit"
              disabled={submit.isPending}
              className="w-full rounded-full bg-brand px-4 py-2 text-sm font-bold text-brown-dark shadow-soft transition hover:brightness-95 disabled:opacity-60"
            >
              {submit.isPending ? "Submitting…" : "Submit report"}
            </button>
            <CloseBtn onClose={onClose} />
          </form>
        )}
      </div>
    </div>
  );
}

function CloseBtn({ onClose, label = "Cancel" }: { onClose: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className="w-full rounded-full border border-border px-4 py-2 text-sm font-semibold text-brown-dark transition hover:bg-secondary"
    >
      {label}
    </button>
  );
}
